import React, { useState, useMemo, useCallback } from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  StatusBadge,
  createTextColumn,
  createEnumColumn,
  createCustomColumn,
  createDateColumn,
  useTableState,
  useColumnSettings,
} from '@medprompt/shared';
import { getReferralTypeLabel, getReferralTypeColorFamily } from './ReferralModal';

/** Hidden column keys until the user saves column settings — must match modal `defaultHidden`. */
const REFERRALS_TABLE_DEFAULT_HIDDEN_COLUMNS = ['timestamp'];

const formatNhsId = (nhsId) => {
  if (!nhsId || nhsId.length !== 10) return nhsId;
  return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6)}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return value; }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch { return value; }
};

const compareInstant = (field) => (a, b) => {
  const va = a[field] ? new Date(a[field]).getTime() : 0;
  const vb = b[field] ? new Date(b[field]).getTime() : 0;
  return va - vb;
};

const useReferralTable = (list) => {
  const { referralManager, addNotification, applyReferralPatch, viewMode, ReferralViewMode } = list;

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkMarkCompleteModalOpen, setBulkMarkCompleteModalOpen] = useState(false);
  const [referralsForBulkAction, setReferralsForBulkAction] = useState([]);
  const [bulkMarkCompleteLoading, setBulkMarkCompleteLoading] = useState(false);
  const [columnCustomizationOpen, setColumnCustomizationOpen] = useState(false);

  const {
    sortState: tableSortState,
    setSortState: setTableSortState,
    filterValues: tableFilterValues,
    setFilterValues: setTableFilterValues,
    disabledFilters: tableDisabledFilters,
    setDisabledFilters: setTableDisabledFilters,
    paginationState: tablePaginationState,
    setPaginationState: setTablePaginationState,
    highlightedRows,
    setHighlightedRows
  } = useTableState('ReferralsTable', {
    defaultSortState: { columnKey: 'referredAt', direction: 'asc' }
  });

  const handleBulkMarkComplete = useCallback((selectedReferralsData) => {
    if (selectedReferralsData.length === 0) return;
    const incomplete = selectedReferralsData.filter(r => !r.isComplete);
    if (incomplete.length === 0) {
      addNotification('bulk-complete-none', 'All selected referrals are already complete', { isError: true });
      return;
    }
    setReferralsForBulkAction(incomplete);
    setBulkMarkCompleteModalOpen(true);
  }, [addNotification]);

  const confirmBulkMarkComplete = () => {
    if (referralsForBulkAction.length === 0) return;
    setBulkMarkCompleteLoading(true);

    const promises = referralsForBulkAction.map(r =>
      referralManager.completeReferral(r.referralId)
        .then(() => {
          applyReferralPatch(r.referralId, { isComplete: true });
          addNotification(`bulk-complete-${r.referralId}`, `Referral for ${r.patient?.name} marked complete`);
        })
        .catch(() => addNotification(`bulk-complete-err-${r.referralId}`, `Failed to complete referral for ${r.patient?.name}`, { isError: true }))
    );

    Promise.allSettled(promises).then(() => {
      setSelectedRows(new Set());
      setBulkMarkCompleteModalOpen(false);
      setReferralsForBulkAction([]);
    }).finally(() => {
      setBulkMarkCompleteLoading(false);
    });
  };

  const columns = useMemo(() => [
    createEnumColumn(
      'status',
      'Status',
      (r) => r.isComplete ? 'COMPLETE' : 'PENDING',
      ['PENDING', 'COMPLETE'],
      {
        compulsory: true,
        renderer: (value) => (
          <StatusBadge
            label={value === 'COMPLETE' ? 'Complete' : 'Pending'}
            colorFamily={value === 'COMPLETE' ? 'green' : 'yellow'}
            size="normal"
          />
        ),
        sortable: true,
        compareFn: (a, b) => {
          if (a.isComplete === b.isComplete) return 0;
          return a.isComplete ? 1 : -1;
        }
      }
    ),

    createTextColumn('patientName', 'Patient Name', (r) => r.patient?.name || ''),

    createCustomColumn(
      'nhsId',
      'NHS Number',
      (r) => r.patient?.nhsId || '',
      (nhsId) => <span className="referral-nhs-cell">{formatNhsId(nhsId) || '—'}</span>,
      { sortable: false, filterable: true }
    ),

    createEnumColumn(
      'referralType',
      'Type',
      (r) => r.referralType,
      ['TWO_WEEK_WAIT', 'URGENT', 'ROUTINE'],
      {
        renderer: (type) => (
          <StatusBadge
            label={getReferralTypeLabel(type)}
            colorFamily={getReferralTypeColorFamily(type)}
            size="normal"
          />
        ),
        sortable: true
      }
    ),

    createTextColumn(
      'text',
      'Notes',
      (r) => {
        const text = r.text || '';
        return text.length > 100 ? text.substring(0, 100) + '...' : text;
      },
      {
        sortable: false,
        filterable: false
      }
    ),

    createDateColumn(
      'referredAt',
      'Date of Referral',
      (r) => r.referredAt,
      {
        compulsory: true,
        renderer: formatDate,
        compareFn: compareInstant('referredAt')
      }
    ),

    createDateColumn(
      'completedAt',
      'Date Completed',
      (r) => r.completedAt,
      {
        compulsory: true,
        renderer: formatDateTime,
        compareFn: compareInstant('completedAt')
      }
    ),

    createDateColumn(
      'timestamp',
      'Created at',
      (r) => r.timestamp,
      {
        renderer: formatDateTime,
        compareFn: compareInstant('timestamp')
      }
    )
  ], []);

  const {
    columnOrder,
    hiddenColumns,
    saveSettings
  } = useColumnSettings('ReferralsTable', columns, REFERRALS_TABLE_DEFAULT_HIDDEN_COLUMNS);

  const hiddenColumnsSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

  const bulkActions = useMemo(() => [
    {
      key: 'markComplete',
      label: 'Mark Complete',
      icon: faCheck,
      onClick: handleBulkMarkComplete
    }
  ], [handleBulkMarkComplete]);

  return {
    columns,
    columnOrder,
    hiddenColumnsSet,
    defaultHidden: REFERRALS_TABLE_DEFAULT_HIDDEN_COLUMNS,
    saveSettings,
    columnCustomizationOpen,
    setColumnCustomizationOpen,
    tableSortState,
    setTableSortState,
    tableFilterValues,
    setTableFilterValues,
    tableDisabledFilters,
    setTableDisabledFilters,
    tablePaginationState,
    setTablePaginationState,
    selectedRows,
    setSelectedRows,
    highlightedRows,
    setHighlightedRows,
    bulkActions,
    bulkMarkCompleteModalOpen,
    setBulkMarkCompleteModalOpen,
    referralsForBulkAction,
    setReferralsForBulkAction,
    bulkMarkCompleteLoading,
    confirmBulkMarkComplete,
    emptyMessage: viewMode === ReferralViewMode.ARCHIVE
      ? 'No archived referrals found.'
      : 'No active referrals found.',
  };
};

export default useReferralTable;
